/**
 * React Query hooks for Dashboard data fetching.
 *
 * First usage of @tanstack/react-query in the codebase.
 * QueryClient is configured in main.tsx with:
 *   staleTime: 5min, retry: 1, refetchOnWindowFocus: false
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getFileDetail, listFiles } from '@/services/fileService';
import { fetchReviewQueue } from '@/services/kineticService';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllRead,
} from '@/services/notificationService';

// ---------------------------------------------------------------------------
// Query keys — stable references for cache management
// ---------------------------------------------------------------------------

export const dashboardKeys = {
  all: ['dashboard'] as const,
  reviewQueue: (limit: number) => [...dashboardKeys.all, 'review-queue', { limit }] as const,
  activity: (pageSize: number) => [...dashboardKeys.all, 'activity', { pageSize }] as const,
  documents: (pageSize: number) =>
    [...dashboardKeys.all, 'documents', { pageSize, source: 'upload' }] as const,
  fileDetail: (fileId: string | null) => [...dashboardKeys.all, 'file-detail', fileId] as const,
  unreadCount: () => [...dashboardKeys.all, 'unread-count'] as const,
};

// ---------------------------------------------------------------------------
// Panel A: Human Review Queue
// ---------------------------------------------------------------------------

export function useReviewQueue(limit = 20) {
  return useQuery({
    queryKey: dashboardKeys.reviewQueue(limit),
    queryFn: () => fetchReviewQueue(limit),
  });
}

// ---------------------------------------------------------------------------
// Panel B: Recent Activity Feed (notifications)
// ---------------------------------------------------------------------------

export function useRecentActivity(pageSize = 10) {
  return useQuery({
    queryKey: dashboardKeys.activity(pageSize),
    queryFn: () => fetchNotifications(1, pageSize),
  });
}

// ---------------------------------------------------------------------------
// Panel C: Recent Uploads
//
// Scoped to manual uploads (source='upload') — the right sidebar is meant to
// surface user-visible docs and showcase the Reducto parsing ViewModal.
// Connector syncs and Drive crawls have their own tabs on /documents.
// ---------------------------------------------------------------------------

export function useRecentDocuments(pageSize = 10) {
  return useQuery({
    queryKey: dashboardKeys.documents(pageSize),
    queryFn: () => listFiles({ pageSize, source: 'upload' }),
  });
}

// ---------------------------------------------------------------------------
// File Detail (document detail modal)
// ---------------------------------------------------------------------------

export function useFileDetail(fileId: string | null) {
  return useQuery({
    queryKey: dashboardKeys.fileDetail(fileId),
    queryFn: () => getFileDetail(fileId!),
    enabled: !!fileId,
  });
}

// ---------------------------------------------------------------------------
// Panel D: Notification Bell
// ---------------------------------------------------------------------------

export function useUnreadCount() {
  return useQuery({
    queryKey: dashboardKeys.unreadCount(),
    queryFn: fetchUnreadCount,
    refetchInterval: 60_000, // Poll every 60s for the bell badge
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.unreadCount() });
      // Invalidate all activity queries regardless of pageSize
      queryClient.invalidateQueries({ queryKey: [...dashboardKeys.all, 'activity'] });
    },
  });
}
